import { actionChannel, call, put, select, take } from "redux-saga/effects";

import { _sharedActionsConstantsMainTree, _sharedActionsMainTree } from "./actions";
import { consoleLogError } from "../../utils/console";
import { getBookById, getDefaultLanguage } from "../saga-functions";
import {
  selectorMainTree,
  selectorMainTreeBibleVersions,
  selectorMainTreeBookLangMap,
  selectorMainTreeBooks,
  selectorMainTreeDictionaries,
  selectorMainTreeSgBooksMap,
} from "./selectors";
import { findNode, getBookIdNumber } from "../../utils/content/content";

import { _sharedActionsSystem } from "../system/actions";
import {
  getBiblesAllLangsRequest,
  getBooksRequest,
  getFolderBooksRequest,
  getSGBooksRequest,
} from "@/shared/api/content/books/requests";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { SETTING } from "@/shared/utils/settings/constants";
import { getLanguageDefault } from "@/shared/utils/user-agent";
import { getLanguagesRequest } from "@/shared/api/not-sorted";
import { getFoldersRequest } from "@/shared/api/content/languages/requests";
import { fetchDictionariesRequest } from "@/shared/api/content/common";
import { makeLoaderIdBook } from "./utils";

const scope = "Shared Saga Main Tree";

export function* _sharedWorkerFetchBook(action) {
  const nBookId = action.data;
  const loaderId = makeLoaderIdBook(nBookId);

  try {
    const book = yield getBookById(nBookId);
    yield put(_sharedActionsSystem.loading(loaderId, false, true));

    if (book) {
      const bookWithRealTypeProperty = yield call(_sharedFetchFolderForBookWorker, {
        data: { book },
      });
      yield put(_sharedActionsMainTree.addBooks(bookWithRealTypeProperty));
      return bookWithRealTypeProperty;
    }

    return book;
  } catch (error) {
    consoleLogError(scope, "Error: ", error);
    return false;
  } finally {
    yield put(_sharedActionsSystem.loading(loaderId, true));
  }
}

export function* _sharedWorkerFetchBooksByIds(booksIds) {
  if (!booksIds) {
    return null;
  }

  const books = yield select(selectorMainTreeBooks);
  const booksOut = [];
  const booksToFetch = [];

  booksIds.forEach((bookId) => {
    const book = books.find((item) => item.id === bookId);
    if (book) {
      booksOut.push(book);
    } else {
      booksToFetch.push(getBookIdNumber(bookId));
    }
  });

  if (booksToFetch.length > 0) {
    try {
      const fetchedBooks = yield call(getBooksRequest, booksToFetch);
      booksOut.push(...fetchedBooks);
      yield put(_sharedActionsMainTree.addBooks(booksOut));
    } catch (error) {
      consoleLogError(scope, "loadBooksDataWorker_error", error);
    }
  }

  return booksOut;
}

export function* _sharedWatcherFetchBooksByIds() {
  const requestChan = yield actionChannel(_sharedActionsConstantsMainTree.FETCH_BOOKS_BY_IDS);
  while (true) {
    const action = yield take(requestChan);
    const { booksIds, loaderId } = action.data;
    if (loaderId) {
      yield put(_sharedActionsSystem.loading(loaderId));
    }
    yield call(_sharedWorkerFetchBooksByIds, booksIds);
    if (loaderId) {
      yield put(_sharedActionsSystem.loading(loaderId, true));
    }
  }
}

export function* _sharedWorkerFetchSGBooks() {
  try {
    const sgBooksMap = yield select(selectorMainTreeSgBooksMap);
    if (Object.keys(sgBooksMap).length === 0) {
      const sgBooks = yield call(getSGBooksRequest);

      if (sgBooks?.length) {
        yield put(_sharedActionsMainTree.fetchSGBooksSuccess(sgBooks));
      }
    }
  } catch (error) {
    console.log("ERROR _sharedWorkerFetchSGBooks", error);
  }
}

export function* _sharedWorkerFetchBibles() {
  try {
    const bibles = yield select(selectorMainTreeBibleVersions);

    if (bibles.length === 0) {
      const biblesNew = yield call(getBiblesAllLangsRequest);
      yield put(_sharedActionsMainTree.fetchBiblesSuccess(biblesNew));
    }
  } catch (error) {
    console.log("ERROR _sharedWorkerFetchBibles", error);
  }
}

export function* _sharedWorkerFetchDictionaries() {
  try {
    const dictionaries = yield select(selectorMainTreeDictionaries);

    if (dictionaries.length === 0) {
      const payload = yield call(fetchDictionariesRequest);
      yield put(_sharedActionsMainTree.fetchDictionariesSuccess(payload));
    }
  } catch (error) {
    console.log("ERROR _sharedWorkerFetchDictionaries", error);
  }
}

export function* _sharedWorkerFetchLanguages() {
  try {
    const mainTree = yield select(selectorMainTree);

    if (mainTree.length === 0) {
      const defLang = yield call(getDefaultLanguage);

      const payload = yield call(getLanguagesRequest, defLang);

      yield put(_sharedActionsMainTree.fetchMainTreeSuccess(payload));

      return payload;
    }

    return mainTree;
  } catch (error) {
    console.log("ERROR _sharedWorkerFetchLanguageFolders", error);
  }
}

export function* _sharedWorkerFetchLanguageFolders(action) {
  try {
    let lang = action.data;

    if (!lang) {
      lang = yield call(getDefaultLanguage);
    }

    const languages = yield call(_sharedWorkerFetchLanguages);

    const langSuitable = findNode(lang, languages);

    if (langSuitable && (!langSuitable.children || !langSuitable.children.length)) {
      langSuitable.children = yield call(getFoldersRequest, lang);
      yield put(_sharedActionsMainTree.fetchMainTreeSuccess([...languages]));
    }

    return {
      languageWithFoldersAttached: langSuitable,
    };
  } catch (error) {
    console.log("ERROR _sharedWorkerFetchLanguageFolders", error);
  }
}

export function* _sharedWorkerFetchMainTreeAllData(lang) {
  try {
    yield call(_sharedWorkerFetchBibles);
    yield call(_sharedWorkerFetchDictionaries);
    yield call(_sharedWorkerFetchSGBooks);

    const { languageWithFoldersAttached } = yield call(_sharedWorkerFetchLanguageFolders, {
      data: lang,
    });

    return {
      languageWithFoldersAttached,
    };
  } catch (error) {
    console.log("ERROR _sharedWorkerFetchMainTreeAllData", error);
  }
}

export function* _sharedFetchFolderForBookWorker(action) {
  const { book } = action.data;
  const mainTree = yield select(selectorMainTree);
  const bookLangMap = yield select(selectorMainTreeBookLangMap);

  let langs = [...mainTree];
  let folder = findNode(book.folder_id, mainTree);
  let bookFinal = book;

  if (!folder) {
    if (mainTree.length === 0) {
      const libraryLanguages = yield select(selectorSetting(SETTING.libraryLanguages.id));
      const defLang = getLanguageDefault(libraryLanguages);

      langs = yield call(getLanguagesRequest, defLang);

      yield put(_sharedActionsMainTree.fetchFoldersToRootSuccess(langs));
    }
    const folders = yield call(getFoldersRequest, book.lang);

    if (folders) {
      langs.find((item) => item.id === book.lang).children = folders;
      folder = findNode(book.folder_id, langs);
    }
  }
  let books;
  if (folder) {
    const parentHolder = [];
    let bookFind = findNode(book.id, folder, "id", parentHolder);

    if (!bookFind) {
      books = yield call(getFolderBooksRequest, book.folder_id);
      if (books) {
        folder.children = books;

        for (const book of books) {
          bookLangMap[book.id] = book.lang;
        }

        bookFind = findNode(book.id, folder, "id", parentHolder);
      }
    }

    bookFind.realType = folder.className;
    bookFind.children = book.children;
    bookFind.chapters = book.chapters;

    /* ---- Spread the new book item to invoke Redux updates. ---- */
    const bookIndex = folder.children.findIndex((item) => item === bookFind);

    if (bookIndex !== -1) {
      folder.children[bookIndex] = { ...bookFind };
    }

    bookFinal = bookFind;
  }

  yield put(
    _sharedActionsMainTree.fetchFoldersToRootSuccess(langs, {
      bookLangMap,
      books,
    })
  );

  return bookFinal;
}
